import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom';
import Separator from '@/components/Divider'
import { ShowUserPasswordModal } from '@/components/Modals/ShowUserPasswordModal'
import Pagination from '@/components/Table/TablePagination'
import frontendConfig from '@/config/frontend'
import {
    CodecRoleType,
    ProductType,
    IntranetRoleType
} from '@/pages/Intranet/Companies'
import ApiService from '@/services/Api'
import {
    EditOutlined as EditIcon, Add as AddIcon,
    DeleteOutline as DeleteIcon,
    LaunchOutlined as AccessUserAccountIcon,
    VpnKeyOutlined as VpnKeyIcon,
    LockOpenOutlined as LockOpenIcon,
    BadgeOutlined as CompanyRoleIcon
} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Divider,
    Typography,
    DialogContent,
    Chip,
    Select
} from '@mui/material'
import ReactSelect from 'react-select' // Importando como ReactSelect em vez de Select
import useStyles from './style'
import { maskPhone, unMask } from '@/components/Mask'
import UserFormDialog, {
    defaultVisibleRadioStatuses,
    IRadio,
    IUser,
    IUserPermissions,
    isExemptEmail,
    PermissionFamily,
    PermissionType,
    radioStatusColors,
    radioStatusLabels,
    RadioStatus
} from '@/components/Users/UserFormDialog';
import UserCompanyDialog from '@/components/Users/UserCompanyDialog';

const Users: React.FC = () => {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [radios, setRadios] = useState<IRadio[]>([])
    const [openedUserDialog, setOpenedUserDialog] = useState(false)
    const [user, setUser] = useState<IUser | null>(null)
    const [newPassword, setNewPassword] = useState<string>('')
    const [updatePassword, setUpdatePassword] = useState<string>('')
    const [loadingUser, setLoadingUser] = useState(false)
    const [betaTester, setBetaTester] = useState<boolean | undefined>(false)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [isOpenNewPasswordModal, setIsOpenNewPasswordModal] = useState(false)
    const [isOpenUpdatePasswordModal, setIsOpenUpdatePasswordModal] = useState(false)
    const [companies, setCompanies] = useState<string[]>([])
    const [users, setUsers] = useState<IUser[]>([])
    const [openedCompaniesDialog, setOpenedCompaniesDialog] = useState(false)
    const [isMetricsDialogOpen, setIsMetricsDialogOpen] = useState(false)
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null)
    const [isLoadingUpdatePassword, setIsLoadingUpdatePassword] = useState(false)
    const [isLoadingUsers, setIsLoadingUsers] = useState(false)
    const [showOnlyDeletedUsers, setShowOnlyDeletedUsers] = useState<boolean>(false)
    const [visibleRadioStatuses, setVisibleRadioStatuses] = useState<RadioStatus[]>(defaultVisibleRadioStatuses)
    const location = useLocation();
    const { companyUuid } = location.state || {}; // Acessando o estado

    const [userPermissions, setUserPermissions] = useState<IUserPermissions>({
        audience: { read: false, write: false, delete: false, update: false },
        campaigns: { read: false, write: false, delete: false, update: false },
        inventory: { read: false, write: false, delete: false, update: false },
        programs: { read: false, write: false, delete: false, update: false },
        whatsapp: { read: false, write: false, delete: false, update: false },
        studio: { read: false, write: false, delete: false, update: false },
        dial_campaigns: { read: false, write: false, delete: false, update: false },
        offline_campaigns: { read: false, write: false, delete: false, update: false }
    });

    const permissionLabels: Record<PermissionType, string> = {
        read: 'Ler',
        update: 'Editar',
        write: 'Criar',
        delete: 'Apagar'
    };

    const toggleAllPermissions = (family: PermissionFamily, checked: boolean) => {
        setUserPermissions(prev => ({
            ...prev,
            [family]: {
                read: checked,
                update: checked,
                write: checked,
                delete: checked
            }
        }));
    };

    const isAllPermissionsChecked = (family: PermissionFamily) => {
        return Object.values(userPermissions[family]).every(value => value);
    };


    useEffect(() => {
        if (companyUuid) {
            handleOpenUserDialog()
            setCompanies([companyUuid])
        }
    }, [companyUuid]);

    const [
        openedRequestPasswordRecoveryDialog,
        setOpenedRequestPasswordRecoveryDialog
    ] = useState<boolean>(false)
    const openCompaniesDialog = (user: IUser) => {
        setSelectedUser(user)
        setOpenedCompaniesDialog(true)
    }

    const handleCloseCompaniesDialog = () => {
        setOpenedCompaniesDialog(false)
        setSelectedUser(null)
    }

    const ITEM_HEIGHT = 48
    const ITEM_PADDING_TOP = 8

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250
            }
        }
    }

    const fetchRadios = async () => {
        try {
            const response = await ApiService.get('/company/list')
            return response.data
        } catch (error) {
            console.error('Erro ao buscar rádios: ', error)
            return []
        }
    }

    useEffect(() => {
        const loadRadios = async () => {
            const fetchedRadios = await fetchRadios()
            setRadios(fetchedRadios)
        }
        loadRadios()
    }, [])

    const toggleRadioStatusVisibility = (status: RadioStatus) => {
        setVisibleRadioStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        )
    }

    // Adicione esta função para transformar os dados para o formato do react-select
    // Rádios com status fora de `visibleRadioStatuses` não aparecem como opção
    const radioOptions = radios
        .filter(radio => visibleRadioStatuses.includes(radio.status || 'customer'))
        .map(radio => ({
            value: radio.uuid,
            label: `${radio.name} - ${radio.address.city}`,
            status: radio.status
        }));

    // Função para obter as opções selecionadas
    // Usa a lista completa de rádios (não a filtrada por status) para que uma rádio já
    // selecionada continue aparecendo como chip mesmo se o filtro de status escondê-la depois
    const getSelectedOptions = () => {
        return radios
            .filter(radio => companies.includes(radio.uuid))
            .map(radio => ({
                value: radio.uuid,
                label: `${radio.name} - ${radio.address.city}`,
                status: radio.status
            }));
    };

    // Substitua esta função
    const handleChange = (selectedOptions: any) => {
        // Se nada for selecionado, define um array vazio
        if (!selectedOptions) {
            setCompanies([]);
            return;
        }

        // Se for múltipla seleção, mapeia os valores
        if (Array.isArray(selectedOptions)) {
            // Verificar se o usuário atual é um dos emails especiais
            const isExemptUser = isExemptEmail(user?.email);

            // Limitar a 25 seleções apenas se não for um usuário isento
            if (!isExemptUser && selectedOptions.length > 75) {
                enqueueSnackbar('Não é possível selecionar mais de 25 rádios.', { variant: 'warning' });
                return;
            }
            const values = selectedOptions.map(option => option.value);
            setCompanies(values);
        } else {
            // Se for seleção única
            setCompanies([selectedOptions.value]);
        }
    }

    const fetchUsers = async () => {
        try {
            const response = await ApiService.get('/user/list')
            return response.data
        } catch (error) {
            console.error('Erro ao buscar usuários: ', error)
            return []
        }
    }

    useEffect(() => {
        const loadUsers = async () => {
            const fetchedUsers = await fetchUsers()
            setUsers(fetchedUsers)
        }
        loadUsers()
    }, [])

    const filteredUsers = users.filter((user) => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase()

        const isDeleted = user.deletedAt !== null
        if (showOnlyDeletedUsers && !isDeleted) return false
        if (!showOnlyDeletedUsers && isDeleted) return false

        const matchesName = user.name.toLowerCase().includes(lowerCaseSearchTerm)

        const matchesCompany = user.companyUuids.some((uuid) => {
            const company = radios.find((company: IRadio) => company.uuid === uuid)
            return company && company.name.toLowerCase().includes(lowerCaseSearchTerm)
        })

        const matchesEmail = user.email.toLowerCase().includes(lowerCaseSearchTerm)

        return matchesName || matchesCompany || matchesEmail
    })

    // Correção no método handleOpenUserDialog

    const handleOpenUserDialog = (userToEdit?: IUser) => {
        setNewPassword('')
        setBetaTester(user?.isBetaTester)
        setUser((prevUser) => {
            if (!prevUser) {
                return null
            }

            return {
                ...prevUser,
                product: 'codec',
                role: 'communicator',
                isBetaTester: false
            }
        })

        // Inicializa todas as permissões incluindo a nova 'offline_campaigns'
        const defaultPermissions = {
            audience: { read: false, write: false, delete: false, update: false },
            campaigns: { read: false, write: false, delete: false, update: false },
            inventory: { read: false, write: false, delete: false, update: false },
            programs: { read: false, write: false, delete: false, update: false },
            whatsapp: { read: false, write: false, delete: false, update: false },
            studio: { read: false, write: false, delete: false, update: false },
            dial_campaigns: { read: false, write: false, delete: false, update: false },
            offline_campaigns: { read: false, write: false, delete: false, update: false }
        };

        if (!userToEdit) {
            setUserPermissions(defaultPermissions);
        } else if (userToEdit.permissions) {
            // Se o usuário existente não tiver a permissão 'offline_campaigns', adiciona ela
            const updatedPermissions = {
                ...defaultPermissions,
                ...userToEdit.permissions,
                // Garante que 'offline_campaigns' existe mesmo que não esteja no objeto original
                offline_campaigns: userToEdit.permissions.offline_campaigns || { read: false, write: false, delete: false, update: false }
            };
            setUserPermissions(updatedPermissions);
        }

        const initialUser = userToEdit || {
            uuid: '',
            companyUuid: '',
            name: '',
            email: '',
            product: 'metrics',
            role: 'admin',
            phoneNumber: null,
            isReportsEnabled: false,
            pictureKey: null,
            preferences: null,
            isBetaTester: false,
            isAccountManager: false,
            createdAt: '',
            lastHeardAt: null,
            updatedAt: '',
            companyUuids: [],
            permissions: defaultPermissions // Usa as permissões padrão para novos usuários
        }

        setUser(initialUser)
        // Dentro da função handleOpenUserDialog, substitua a parte que inicializa as companies

        if (initialUser.companyUuids.length === 0 && initialUser.companyUuid) {
            setCompanies([initialUser.companyUuid])
        } else {
            setCompanies(initialUser.companyUuids)
        }
        setOpenedUserDialog(true)
    }

    const handleOpenUpdatePasswordModal = async (userId: string | undefined) => {
        setOpenedRequestPasswordRecoveryDialog(false)
        setIsLoadingUpdatePassword(true)
        try {
            const { data } = await ApiService.patch(`/user/password/${userId}`)

            setUpdatePassword(data.newPassword)
            setIsOpenUpdatePasswordModal(true)
        } catch (error: any) {
            enqueueSnackbar(
                error?.data ||
                'Ocorreu um erro ao tentar alterar a senha do usuário.',
                {
                    variant: 'error'
                }
            )
        } finally {
            setIsLoadingUpdatePassword(false)
        }
    }

    const handleAccessUserAccount = async (user: IUser, version: 'legacy' | 'default' = 'default') => {
        try {
            const { data: token } = await ApiService.post(
                `/company/${user.companyUuid}/user/${user.uuid}/token`
            )

            const versionSuffix = version === 'default' ? '' : '-v1'
            const versionPath = version === 'default' ? '/login' : '/'
            const url = `http://${user.product}${versionSuffix}.${frontendConfig.frontendBaseUrl}${versionPath}?token=${token}`

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            window.open(url, '_blank')?.focus()
        } catch (error: any) {
            enqueueSnackbar(
                error?.data ||
                'Ocorreu um erro ao tentar acessar a conta do usuário.',
                {
                    variant: 'error'
                }
            )
        }
    }

    const handleCloseUserDialog = () => {
        setOpenedUserDialog(false)
        setUser(null)
    }

    const handleCloseMetricsDialog = () => {
        setIsMetricsDialogOpen(false)
        setUser(null)
    }

    interface ValidationErrors {
        name?: string;
        email?: string;
        product?: string;
        companyUuids?: string;
    }

    const validateUser = (user: IUser): ValidationErrors => {
        const errors: ValidationErrors = {}

        if (!user?.name) {
            errors.name = 'O campo nome é obrigatório.'
        }
        if (!user?.email) {
            errors.email = 'O campo email é obrigatório.'
        }
        if (!user?.product) {
            errors.product = 'O campo produto é obrigatório.'
        }

        if (
            user.product !== 'intranet' &&
            (!user?.companyUuids || user?.companyUuids.length === 0)
        ) {
            errors.companyUuids = 'Pelo menos uma companhia deve ser selecionada.'
        }

        // Verificar se o usuário atual é um dos emails especiais
        const isExemptUser = isExemptEmail(user?.email);

        // Adicionar validação para o limite de 25 rádios apenas se não for um usuário isento
        if (!isExemptUser && user?.companyUuids && user.companyUuids.length > 75) {
            errors.companyUuids = 'Não é possível selecionar mais de 25 rádios.'
        }

        return errors
    }

    const renderPermissionsSection = () => {
        const permissionFamilies: PermissionFamily[] = ['audience', 'campaigns', 'inventory', 'programs', 'whatsapp', 'studio', 'dial_campaigns', 'offline_campaigns'];
        const permissionTypes: PermissionType[] = ['read', 'write', 'delete', 'update'];

        const familyLabels: Record<PermissionFamily, string> = {
            audience: 'Audiência',
            campaigns: 'Campanhas',
            inventory: 'Inventário',
            programs: 'Programas',
            whatsapp: 'WhatsApp',
            studio: 'Studio',
            dial_campaigns: 'Comprova Play',
            offline_campaigns: 'Campanhas Offline'
        };

        return (
            <Grid container spacing={1} style={{ padding: '16px' }}>
                <Grid item xs={12}>
                    <Typography variant="h6">Permissões</Typography>
                </Grid>
                {permissionFamilies.map(family => {
                    const allChecked = Object.values(userPermissions[family]).every(v => v);

                    return (
                        <Grid item xs={12} key={family} container alignItems="center" spacing={1}>
                            <Grid item xs={2}>
                                <Typography variant="subtitle1">
                                    {familyLabels[family]}:
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    id={`${family}_all_permissions_button`}
                                    style={{ padding: 1, fontSize: 11 }}
                                    variant={allChecked ? "contained" : "outlined"}
                                    color="primary"
                                    onClick={() => {
                                        setUserPermissions(prev => ({
                                            ...prev,
                                            [family]: {
                                                read: !allChecked,
                                                write: !allChecked,
                                                delete: !allChecked,
                                                update: !allChecked
                                            }
                                        }));
                                    }}
                                >
                                    Todos
                                </Button>
                            </Grid>
                            {permissionTypes.map(type => (
                                <Grid item key={`${family}-${type}`}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id={`${family}_${type}_permission_checkbox`}
                                                checked={userPermissions[family][type]}
                                                onChange={(e) => {
                                                    setUserPermissions(prev => ({
                                                        ...prev,
                                                        [family]: {
                                                            ...prev[family],
                                                            [type]: e.target.checked
                                                        }
                                                    }));
                                                }}
                                            />
                                        }
                                        label={permissionLabels[type]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    const saveUser = async () => {
        const newUser: IUser = {
            uuid: user?.uuid || undefined,
            companyUuid: companies[0],
            companyUuids: companies,
            name: user?.name || '',
            email: user?.email || '',
            product: user?.product || 'metrics',
            role: user?.role || 'admin',
            isBetaTester: user?.isBetaTester || false,
            phoneNumber: user?.phoneNumber || null,
            isReportsEnabled: user?.isReportsEnabled || false,
            isAccountManager: user?.isAccountManager || false,
            pictureKey: user?.pictureKey || null,
            preferences: user?.preferences || null,
            createdAt: user?.createdAt || '',
            lastHeardAt: user?.lastHeardAt || null,
            updatedAt: user?.updatedAt || '',
            permissions: userPermissions  // Add permissions to the user object
        }

        const errors = validateUser(newUser)

        if (Object.keys(errors).length > 0) {
            Object.values(errors).forEach(error => {
                enqueueSnackbar(error, { variant: 'error' })
            })
            return
        }

        const isNewUser = !user?.uuid

        try {
            const response = await ApiService.post('/user', newUser)
            if (response.data) {
                const fetchedUsers = await fetchUsers()
                setUsers(fetchedUsers)
                enqueueSnackbar('Usuário salvo com sucesso.', { variant: 'success' })
                handleCloseUserDialog()

                if (isNewUser) {
                    setNewPassword(response.data.password)
                    setIsOpenNewPasswordModal(true)
                }
            }
        } catch (error: any) {
            console.error('Erro ao salvar usuário:', error)
            enqueueSnackbar(`${error.data}`, { variant: 'error' })
        }
    }

    const handleDeleteUser = async () => {
        setLoadingDeleteUser(true)
        try {
            await ApiService.delete(
                `/user/${user?.uuid}`
            )
            setOpenedDeleteUserDialog(false)
            setUser(prevUser => {
                if (!prevUser) return null

                return {
                    product: 'codec',
                    role: 'communicator',
                    isBetaTester: false,
                    companyUuid: prevUser.companyUuid,
                    name: prevUser.name,
                    email: prevUser.email,
                    phoneNumber: prevUser.phoneNumber,
                    isReportsEnabled: prevUser.isReportsEnabled || false,
                    isAccountManager: prevUser.isAccountManager || false,
                    pictureKey: prevUser.pictureKey,
                    preferences: prevUser.preferences,
                    createdAt: prevUser.createdAt,
                    lastHeardAt: prevUser.lastHeardAt,
                    updatedAt: new Date().toISOString(),
                    companyUuids: prevUser.companyUuids
                }
            })

            enqueueSnackbar('Usuário removido com sucesso.', {
                variant: 'success'
            })
        } catch (error: any) {
            enqueueSnackbar(
                error?.data || 'Ocorreu um erro ao remover o usuário.',
                {
                    variant: 'error'
                }
            )
        }

        setLoadingDeleteUser(false)
        const fetchedUsers = await fetchUsers()
        setUsers(fetchedUsers)
    }

    const handleUnlockUser = async (user: IUser) => {
        try {
            await ApiService.patch(`/user/${user?.uuid}`)
            enqueueSnackbar('Usuário desbloqueado com sucesso.', {
                variant: 'success'
            })
        } catch (error: any) {
            enqueueSnackbar(
                error?.data || 'Ocorreu um erro ao desbloquear o usuário.',
                {
                    variant: 'error'
                }
            )
        }
    }

    const [openedDeleteUserDialog, setOpenedDeleteUserDialog] =
        useState<boolean>(false)
    const [loadingDeleteUser, setLoadingDeleteUser] = useState<boolean>(false)

    const [openedUserCompanyDialog, setOpenedUserCompanyDialog] = useState<boolean>(false)
    const [userCompanyDialogUser, setUserCompanyDialogUser] = useState<IUser | null>(null)

    const openUserCompanyDialog = (user: IUser) => {
        setUserCompanyDialogUser(user)
        setOpenedUserCompanyDialog(true)
    }

    const handleCloseUserCompanyDialog = () => {
        setOpenedUserCompanyDialog(false)
        setUserCompanyDialogUser(null)
    }

    const handleCompanyDialogSaved = async () => {
        setIsLoadingUsers(true)
        const fetchedUsers = await fetchUsers()
        setUsers(fetchedUsers)
        setIsLoadingUsers(false)
    }

    const openDeleteUserDialog = async (user: IUser) => {
        setUser(user)
        setOpenedDeleteUserDialog(true)
    }

    const selectProduct = ({
        target
    }: {
        target: {
            name?: string | undefined;
            value: unknown;
        };
    }) => {
        setUser(prevUser => {
            if (!prevUser) return null

            const isBetaTesterValue = target.value === 'metrics' ? (betaTester ?? false) : false

            return {
                ...prevUser,
                product: target.value as ProductType,
                isBetaTester: isBetaTesterValue
            }
        })
    }

    const renderCompanies = (user: IUser) => {
        if (user.companyUuids.length > 3) {
            return (
                <Button onClick={() => openCompaniesDialog(user)}>
                    Ver Rádios ({user.companyUuids.length})
                </Button>
            )
        }

        if (user.companyUuids.length > 0) {
            return user.companyUuids
                .map(uuid => radios.find(r => r.uuid === uuid)?.name)
                .filter(Boolean)
                .join(', ')
        }

        return radios.find(r => r.uuid === user.companyUuid)?.name || 'Nenhuma rádio'
    }

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(6)
    const handleChangePage = (event: any, newPage: React.SetStateAction<number>) => {
        setPage(newPage)
    }
    const handleChangeRowsPerPage = (event: { target: { value: string; }; }) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const usersToDisplay = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    return (
        <>

            <Grid container justifyContent="space-between">
                <Grid>
                    <TextField
                        id="user_search_field"
                        label="Buscar por usuário, rádio ou e-mail"
                        size="small"
                        color="primary"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={classes.searchInput}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    />

                    <FormControlLabel
                        className={classes.filterEnabledCheckbox}
                        control={
                            <Checkbox
                                id="show_deleted_users_checkbox"
                                checked={showOnlyDeletedUsers}
                                onChange={({ target }) => setShowOnlyDeletedUsers(target.checked)}
                                color="default"
                            />
                        }
                        label={'Mostrar apenas usuários deletados'}
                    />
                </Grid>
                <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenUserDialog()}>
                    Criar Usuário
                </Button>
            </Grid>

            <Separator size={3} />

            <Grid container spacing={4}>
                {/* Tabela de usuários */}
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="users table" >
                            <TableHead>
                                <TableRow sx={{ height: '30px' }}>
                                    <TableCell align="left">Rádio</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell align="center">E-mail</TableCell>
                                    <TableCell align="center">Produto</TableCell>
                                    <TableCell align="center" />
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usersToDisplay.map((user) => (
                                    <TableRow key={user.uuid} sx={{ height: '30px' }}>
                                        <TableCell align="left" >
                                            {renderCompanies(user)}
                                        </TableCell>
                                        <TableCell component="th" scope="row">{user.name}</TableCell>
                                        <TableCell align="center">{user.email}</TableCell>
                                        <TableCell align="center">{user.product}</TableCell>
                                        <TableCell align="center">
                                            {user.isBetaTester && (
                                                <Chip className={classes.betaChip} size="medium" label="BETA" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                            {!!user.deletedAt && (
                                                <IconButton onClick={() => { handleUnlockUser(user) }}>
                                                    <LockOpenIcon />
                                                </IconButton>
                                            )}
                                            <IconButton onClick={() => { setUser(user); setOpenedRequestPasswordRecoveryDialog(true) }} disabled={!!user.deletedAt}>
                                                <VpnKeyIcon />
                                            </IconButton>
                                            {user?.product === 'metrics'
                                                ? (
                                                    <IconButton onClick={() => { setUser(user); setIsMetricsDialogOpen(true) }} title="metrics options" disabled={!!user.deletedAt}>
                                                        <AccessUserAccountIcon />
                                                    </IconButton>
                                                )
                                                : (
                                                    <IconButton onClick={() => handleAccessUserAccount(user)} title={user?.product} disabled={!!user.deletedAt}>
                                                        <AccessUserAccountIcon />
                                                    </IconButton>
                                                )}
                                            <IconButton onClick={() => handleOpenUserDialog(user)} disabled={!!user.deletedAt}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => openUserCompanyDialog(user)} title="Função na rádio" disabled={!!user.deletedAt}>
                                                <CompanyRoleIcon />
                                            </IconButton>
                                            <IconButton onClick={() => openDeleteUserDialog(user)} disabled={!!user.deletedAt}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            count={filteredUsers.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grid>
            </Grid>
            <Dialog
                open={openedCompaniesDialog}
                onClose={handleCloseCompaniesDialog}
            >
                <DialogTitle>Radios Associadas</DialogTitle>
                <DialogContent>
                    <Typography>
                        {selectedUser?.companyUuids
                            .map((uuid) => {
                                const company = radios.find(
                                    (r) => r.uuid === uuid
                                )
                                return company ? company.name : ''
                            })
                            .filter(Boolean)
                            .join(', ')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCompaniesDialog} color="primary">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog para criar/editar usuários */}
            <Dialog open={openedUserDialog}>
                <DialogTitle>
                    {!newPassword
                        ? !user?.uuid
                            ? 'Criando novo usuário'
                            : 'Editando o usuário'
                        : 'Sucesso'}
                </DialogTitle>

                {loadingUser && !user?.uuid && (
                    <Grid container justifyContent="center">
                        <CircularProgress size={80} color="primary" />
                    </Grid>
                )}

                {(!loadingUser || user?.uuid) && !newPassword && user && (
                    <>
                        <Grid container spacing={2} style={{ padding: '16px' }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="user_name_field"
                                    fullWidth
                                    label="Nome"
                                    size="small"
                                    variant="outlined"
                                    value={user.name}
                                    onChange={(e) =>
                                        setUser({ ...user, name: e.target.value })
                                    }
                                    inputProps={{
                                        'data-lpignore': 'true',
                                        'data-1password': 'true',
                                        'data-op-ignore': 'true'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="user_email_field"
                                    fullWidth
                                    label="Email"
                                    size="small"
                                    variant="outlined"
                                    value={user.email}
                                    onChange={(e) =>
                                        setUser({
                                            ...user,
                                            email: e.target.value
                                        })
                                    }
                                    inputProps={{
                                        'data-lpignore': 'true',
                                        'data-1password': 'true',
                                        'data-op-ignore': 'true'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                >
                                    <InputLabel id="product_select_label">Produto</InputLabel>
                                    <Select
                                        id="product_select_field"
                                        value={user.product}
                                        onChange={selectProduct}
                                        variant="outlined"
                                        color="primary"
                                        label="Produto"
                                        className={classes.select}
                                        labelId="product_select_label"

                                    >
                                        <MenuItem value="metrics">
                                            Metrics
                                        </MenuItem>

                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                            >
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                >
                                    <div style={{ position: 'relative' }}>
                                        <label
                                            style={{
                                                position: 'absolute',
                                                backgroundColor: 'white',
                                                padding: '0 4px',
                                                top: '-10px',
                                                left: '10px',
                                                fontSize: '12px',
                                                zIndex: 1,
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                fontFamily: 'Roboto, sans-serif'
                                            }}
                                        >
                                            Rádio
                                        </label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '4px 0 8px' }}>
                                            {(['customer', 'internal', 'trial', 'concurrence'] as RadioStatus[]).map(status => {
                                                const isVisible = visibleRadioStatuses.includes(status)
                                                return (
                                                    <Chip
                                                        key={status}
                                                        id={`radio_status_filter_${status}_chip`}
                                                        size="small"
                                                        clickable
                                                        onClick={() => toggleRadioStatusVisibility(status)}
                                                        variant={isVisible ? 'filled' : 'outlined'}
                                                        style={{
                                                            opacity: isVisible ? 1 : 0.55,
                                                            fontFamily: 'Roboto, sans-serif'
                                                        }}
                                                        label={
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span
                                                                    style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: radioStatusColors[status],
                                                                        display: 'inline-block',
                                                                        flexShrink: 0
                                                                    }}
                                                                />
                                                                {radioStatusLabels[status]}
                                                            </span>
                                                        }
                                                    />
                                                )
                                            })}
                                        </div>
                                        <ReactSelect
                                            key={visibleRadioStatuses.join(',')}
                                            inputId="radio_select_field"
                                            isMulti
                                            value={getSelectedOptions()}
                                            onChange={handleChange}
                                            options={radioOptions}
                                            placeholder="Selecione as rádios..."
                                            classNamePrefix="react-select"
                                            isSearchable
                                            hideSelectedOptions={false}
                                            isClearable={false}
                                            closeMenuOnSelect={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            formatOptionLabel={(option: { value: string; label: string; status?: RadioStatus }) => (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {option.status && (
                                                        <span
                                                            style={{
                                                                width: 7,
                                                                height: 7,
                                                                borderRadius: '50%',
                                                                backgroundColor: radioStatusColors[option.status],
                                                                flexShrink: 0,
                                                                display: 'inline-block'
                                                            }}
                                                        />
                                                    )}
                                                    {option.label}
                                                </span>
                                            )}
                                            isOptionDisabled={() => {
                                                const isExemptUser = isExemptEmail(user?.email);
                                                return !isExemptUser && getSelectedOptions().length >= 25;
                                            }}
                                            styles={{
                                                menuPortal: (baseStyles) => ({
                                                    ...baseStyles,
                                                    zIndex: 9999
                                                }),
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    minHeight: '40px',
                                                    borderRadius: '4px',
                                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                                    boxShadow: 'none',
                                                    fontFamily: 'Roboto, sans-serif',
                                                    '&:hover': {
                                                        border: '1px solid rgba(0, 0, 0, 0.87)'
                                                    }
                                                }),
                                                menu: (baseStyles) => ({
                                                    ...baseStyles,
                                                    zIndex: 9999,
                                                    fontFamily: 'Roboto, sans-serif'
                                                }),
                                                multiValue: (baseStyles) => ({
                                                    ...baseStyles,
                                                    margin: '2px',
                                                    fontFamily: 'Roboto, sans-serif',
                                                    // Garantir que cada multi-value mantenha seu tamanho original
                                                    flexShrink: 0
                                                }),
                                                valueContainer: (baseStyles) => ({
                                                    ...baseStyles,
                                                    padding: '2px 8px',
                                                    overflow: 'auto',
                                                    whiteSpace: 'nowrap',
                                                    flexWrap: 'nowrap',
                                                    fontFamily: 'Roboto, sans-serif',
                                                    // Permitir expansão horizontal infinita
                                                    maxWidth: 'none',
                                                    width: 'auto',
                                                    // Garantir que o container não diminua os itens
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                    '::-webkit-scrollbar': {
                                                        height: '4px'
                                                    },
                                                    '::-webkit-scrollbar-thumb': {
                                                        background: 'rgba(0,0,0,0.2)',
                                                        borderRadius: '2px'
                                                    }
                                                }),
                                                container: (baseStyles) => ({
                                                    ...baseStyles,
                                                    width: '100%',
                                                    minWidth: '250px'
                                                }),
                                                option: (baseStyles, { isSelected }) => ({
                                                    ...baseStyles,
                                                    fontFamily: 'Roboto, sans-serif',
                                                    backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : baseStyles.backgroundColor,
                                                    color: isSelected ? '#1976d2' : baseStyles.color,
                                                    '&:active': {
                                                        backgroundColor: isSelected ? 'rgba(11, 83, 156, 0.56)' : 'rgba(228, 230, 233, 0.16)'
                                                    }
                                                }),
                                                input: (baseStyles) => ({
                                                    ...baseStyles,
                                                    fontFamily: 'Roboto, sans-serif',
                                                    // Evitar que o input seja comprimido
                                                    flexShrink: 0
                                                }),
                                                placeholder: (baseStyles) => ({
                                                    ...baseStyles,
                                                    fontFamily: 'Roboto, sans-serif'
                                                }),
                                                singleValue: (baseStyles) => ({
                                                    ...baseStyles,
                                                    fontFamily: 'Roboto, sans-serif'
                                                })
                                            }}
                                            components={{
                                                // Para garantir que o componente de vários valores não seja redimensionado
                                                MultiValueContainer: ({ children, ...props }) => (
                                                    <div style={{ display: 'flex', flexShrink: 0 }} {...props}>
                                                        {children}
                                                    </div>
                                                )
                                            }}
                                        />
                                    </div>
                                </FormControl>
                                </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="user_phone_field"
                                    className={classes.itemInput}
                                    label="Número de telefone (WhatsApp)" size="small" color="primary" variant="outlined"
                                    value={maskPhone(user.phoneNumber || undefined)}
                                    onChange={({ target }) => setUser({ ...user, phoneNumber: unMask(target.value) })}
                                    inputProps={{
                                        'data-lpignore': 'true',
                                        'data-1password': 'true',
                                        'data-op-ignore': 'true'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="is_reports_enabled_checkbox"
                                            checked={user.isReportsEnabled}
                                            onChange={(e) =>
                                                setUser({
                                                    ...user,
                                                    isReportsEnabled:
                                                        e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Envio automático de relatórios"
                                />
                            </Grid>

                            {renderPermissionsSection()}
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="beta_tester_checkbox"
                                            checked={user.isBetaTester}
                                            onChange={(e) =>
                                                setUser({
                                                    ...user,
                                                    isBetaTester:
                                                        e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Acesso a recursos em beta"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="is_account_manager_checkbox"
                                            checked={user.isAccountManager}
                                            onChange={(e) =>
                                                setUser({
                                                    ...user,
                                                    isAccountManager:
                                                        e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Representante de conta"
                                />
                            </Grid>
                        </Grid>

                        <Divider />

                        <DialogActions>
                            <Button
                                onClick={handleCloseUserDialog}
                                color="primary"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={saveUser}
                                color="primary"
                                variant="contained"
                            >
                                {user.uuid ? 'Salvar' : 'Criar'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <UserCompanyDialog
                open={openedUserCompanyDialog}
                onClose={handleCloseUserCompanyDialog}
                user={userCompanyDialogUser}
                radios={radios}
                onSaved={handleCompanyDialogSaved}
            />

            <ShowUserPasswordModal
                password={newPassword}
                setPassword={setNewPassword}
                isOpenPasswordModal={isOpenNewPasswordModal}
                setIsOpenPasswordModal={setIsOpenNewPasswordModal}
            />

            <ShowUserPasswordModal
                password={updatePassword}
                setPassword={setUpdatePassword}
                isOpenPasswordModal={isOpenUpdatePasswordModal}
                setIsOpenPasswordModal={setIsOpenUpdatePasswordModal}
            />

            <Dialog
                aria-labelledby="simple-dialog-title"
                open={openedDeleteUserDialog}
            >
                <DialogTitle>
                    Tem certeza que deseja remover o usuário {user?.name}
                </DialogTitle>
                <DialogActions>
                    <Button
                        onClick={() => setOpenedDeleteUserDialog(false)}
                        className={classes.deleteUserDialogCancelButton}
                        disabled={loadingDeleteUser}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDeleteUser}
                        color="primary"
                        autoFocus
                        disabled={loadingDeleteUser}
                        endIcon={
                            loadingDeleteUser && (
                                <CircularProgress size={15} color="inherit" />
                            )
                        }
                    >
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                aria-labelledby="simple-dialog-title"
                open={isMetricsDialogOpen && !!user}
                onClose={handleCloseMetricsDialog}
                PaperProps={{
                    style: {
                        padding: '10px 40px'
                    }
                }}
            >
                <DialogTitle>
                    <IconButton
                        onClick={
                            handleCloseMetricsDialog
                        }
                        style={{
                            position:
                                'absolute',
                            top: '5px',
                            right: '5px',
                            color: '#707070'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogActions style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px', width: '200px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: '0px' }}
                        fullWidth
                        onClick={() => {
                            handleAccessUserAccount(user!, 'default')
                            handleCloseMetricsDialog()
                        }}
                    >
                        Metrics
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            handleAccessUserAccount(user!, 'legacy')
                            handleCloseMetricsDialog()
                        }}
                    >
                        Metrics Legado
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                aria-labelledby="simple-dialog-title"
                open={openedRequestPasswordRecoveryDialog}
            >
                <DialogTitle>
                    Tem certeza que deseja solicitar a recuperação de senha para
                    o usuário {user?.name}?
                </DialogTitle>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setOpenedRequestPasswordRecoveryDialog(false)
                        }
                        className={
                            classes.RequestPasswordRecoveryDialogCancelButton
                        }
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => handleOpenUpdatePasswordModal(user?.uuid)}
                        color="primary"
                        autoFocus
                        disabled={isLoadingUpdatePassword}
                        endIcon={
                            isLoadingUpdatePassword && (
                                <CircularProgress size={15} color="inherit" />
                            )
                        }
                    >
                        Solicitar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Users
